// End-of-match results: the stats the server (or the local Game) accumulated, as a full-screen plate —
// verdict, score, duration, medals, one table per team. Pure view-model helpers (medals, totals, html)
// are DOM-free and unit-tested; mountResults is the thin DOM wrapper hud.ts mounts once a winner and
// the stats are both known.
import type { HeroKind } from '../core/heroes.js';
import type { Team } from '../core/layout.js';
import { emptyHeroStats, type HeroStatLine, type HeroStats, type MatchStats } from '../core/stats.js';
import { escapeHtml, playerLabel } from './nameStore.js';
import { t } from './i18n/index.js';
import { heroName, passiveName, relicName, teamName } from './i18n/names.js';
import { STAT_KEYS } from '../shared/matchStats.js';
import { iconImg } from './shop.js';

export type ResultsData = {
  winner: Team;
  score: { a: number; b: number };
  stats: MatchStats;
  viewerTeam: Team;
  viewerId: number | null;
};


export const support = (h: HeroStats) => h.healing + h.shielding;

export type MedalId = 'damage' | 'capture' | 'undying' | 'hunter' | 'healer';
export type Medal = { id: MedalId; title: string; hero: HeroStatLine; value: string };

/* The best hero by `key` (larger wins, ties go to the next key, then to the lower id), or null when nobody
   qualifies. Only candidates passing `eligible` are considered. */
function best(heroes: readonly HeroStatLine[], eligible: (h: HeroStatLine) => boolean, ...keys: ((h: HeroStatLine) => number)[]) {
  let top: HeroStatLine | null = null;
  for (const h of heroes) {
    if (!eligible(h)) continue;
    if (!top) { top = h; continue; }
    for (const k of keys) {
      const d = k(h) - k(top);
      if (d > 0) { top = h; break; }
      if (d < 0) break;
    }
  }
  return top;
}

const int = (v: number) => String(Math.round(v));
export const mmss = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;

/** Medals in display order; one whose category nobody scored in is left out, not awarded for zero. */
export function medals(stats: MatchStats): Medal[] {
  const hs = stats.heroes, out: Medal[] = [];
  const damage = best(hs, h => h.damageToHeroes > 0, h => h.damageToHeroes, h => h.kills);
  if (damage) out.push({ id: 'damage', title: t('medal.damage'), hero: damage, value: t('medal.damageValue', { n: int(damage.damageToHeroes) }) });
  const capture = best(hs, h => h.captures > 0 || h.pointTime >= 1, h => h.captures, h => h.pointTime);
  if (capture) out.push({ id: 'capture', title: t('medal.capture'), hero: capture, value: t('medal.captureValue', { n: capture.captures, time: mmss(capture.pointTime) }) });
  // Someone who never took a hit (never left base) is not "unkillable", just absent.
  const undying = best(hs, h => h.damageTaken > 0, h => -h.deaths, h => h.damageTaken);
  if (undying) out.push({ id: 'undying', title: t('medal.undying'), hero: undying, value: t('medal.undyingValue', { n: undying.deaths, taken: int(undying.damageTaken) }) });
  const hunter = best(hs, h => h.monsterKills > 0, h => h.monsterKills, h => h.damageToMonsters);
  if (hunter) out.push({ id: 'hunter', title: t('medal.hunter'), hero: hunter, value: t('medal.hunterValue', { n: hunter.monsterKills }) });
  const healer = best(hs, h => support(h) >= 1, support);
  if (healer) out.push({ id: 'healer', title: t('medal.healer'), hero: healer, value: t('medal.healerValue', { n: int(support(healer)) }) });
  return out;
}

export function teamTotals(stats: MatchStats, team: Team): HeroStats {
  const t = emptyHeroStats();
  for (const h of stats.heroes) {
    if (h.team !== team) continue;
    for (const k of STAT_KEYS) t[k] += h[k];
  }
  t.bestStreak = Math.max(0, ...stats.heroes.filter(h => h.team === team).map(h => h.bestStreak));
  return t;
}

/** One short phrase per hero, turning `HeroStats.passive` (passives.ts's own `stat` meaning) into a reader's
    tooltip — a hero's own passive is otherwise invisible in this table (it feeds no other column). Decimals:
    metres/seconds read to one decimal, everything else (hp, hit counts) as a whole number. */
const PASSIVE_DECIMALS: Partial<Record<HeroKind, number>> = { rhino: 1, astrologer: 1, ferryman: 1, monk: 1, shadow: 1, birdcatcher: 1 };
function passiveTip(h: HeroStatLine): string {
  const v = h.passive.toFixed(PASSIVE_DECIMALS[h.kind] ?? 0);
  return t('res.passiveTip', { name: passiveName(h.kind), stat: t(`res.passiveStat.${h.kind}` as const, { v }) });
}

const MEDAL_GLYPH: Record<MedalId, string> = { damage: '⚔', capture: '⚑', undying: '⛨', hunter: '☠', healer: '✚' };
const portrait = (kind: HeroKind) => `<img class="res-portrait" src="portraits/${kind}.jpg" alt="" width="480" height="640" loading="lazy">`;
const playerName = (h: HeroStatLine) => (h.name === null ? `<i>${t('res.bot')}</i>` : escapeHtml(playerLabel(h.name)));

function cells(s: HeroStats) {
  return `<td class="res-num kda">${s.kills} / ${s.deaths} / ${s.assists}</td>
    <td class="res-num">${int(s.damageToHeroes)}</td><td class="res-num">${int(s.damageTaken)}</td>
    <td class="res-num" title="${t('res.supportTitle', { heal: int(s.healing), shield: int(s.shielding) })}">${int(support(s))}</td>
    <td class="res-num">${int(s.damageToMonsters)}</td><td class="res-num">${s.captures}</td>
    <td class="res-num">${mmss(s.pointTime)}</td><td class="res-num">${int(s.inkSpent)}</td>`;
}

function teamTableHtml(d: ResultsData, team: Team): string {
  const rows = d.stats.heroes.filter(h => h.team === team).map(h => `
      <tr class="${h.id === d.viewerId ? 'me' : ''}" data-hero="${h.id}">
        <td class="who"><div class="res-who">${portrait(h.kind)}<span><b title="${passiveTip(h)}">${heroName(h.kind)}</b><small>${playerName(h)}</small></span></div></td>
        ${cells(h)}
      </tr>`).join('');
  const won = d.winner === team, relics = d.stats.relics?.[team] ?? [];
  // Only for a team that ever bought a relic: most matches have none.
  const relicLine = relics.length
    ? `<p class="res-relics">${t('res.relics')} ${relics.map(r => `${iconImg(r.line, 'res-relic-ico', 20)}${relicName(r.line)}${r.lost ? t('res.relicLost') : ''}`).join(', ')}</p>` : '';
  return `
    <section class="res-team ${team}${team === d.viewerTeam ? ' own' : ''}" data-team="${team}">
      <h2>${teamName(team)} <span>${d.score[team]}</span>${won ? ` <em>${t('res.teamWon')}</em>` : ''}</h2>${relicLine}
      <table>
        <thead><tr>
          <th class="who">${t('res.col.hero')}</th><th title="${t('res.col.kdaTitle')}">${t('res.col.kda')}</th><th>${t('res.col.dmg')}</th><th>${t('res.col.taken')}</th>
          <th>${t('res.col.heal')}</th><th>${t('res.col.monsters')}</th><th>${t('res.col.captures')}</th><th>${t('res.col.onPoints')}</th><th title="${t('res.col.inkTitle')}">${t('res.col.ink')}</th>
        </tr></thead>
        <tbody>${rows}</tbody>
        <tfoot><tr><td class="who">${t('res.total')}</td>${cells(teamTotals(d.stats, team))}</tr></tfoot>
      </table>
    </section>`;
}

export type ResultsButtons = { primaryLabel: string; secondaryLabel?: string };

export function resultsHtml(d: ResultsData, b: ResultsButtons): string {
  const won = d.winner === d.viewerTeam;
  const other: Team = d.viewerTeam === 'a' ? 'b' : 'a';
  const medalHtml = medals(d.stats).map(m => `
      <li class="res-medal" data-medal="${m.id}">
        <span class="res-glyph" aria-hidden="true">${MEDAL_GLYPH[m.id]}</span>
        <span class="res-medal-title">${m.title}</span>
        ${portrait(m.hero.kind)}
        <span class="res-medal-who"><b>${heroName(m.hero.kind)}</b><small>${playerName(m.hero)}</small></span>
        <span class="res-medal-val">${m.value}</span>
      </li>`).join('');
  return `
    <div class="res-plate" role="dialog" aria-modal="true" aria-labelledby="resTitle">
      <header class="res-head">
        <h1 id="resTitle" class="${won ? 'win' : 'loss'}">${won ? t('res.victory') : t('res.defeat')}</h1>
        <p class="res-score"><span class="${d.viewerTeam}">${teamName(d.viewerTeam)} ${d.score[d.viewerTeam]}</span> : <span class="${other}">${d.score[other]} ${teamName(other)}</span></p>
        <p class="res-time">${t('res.duration', { time: mmss(d.stats.duration) })}</p>
      </header>
      ${medalHtml ? `<ul class="res-medals">${medalHtml}</ul>` : ''}
      <div class="res-tables">${teamTableHtml(d, d.viewerTeam)}${teamTableHtml(d, other)}</div>
      <div class="res-actions">
        ${b.secondaryLabel ? `<button type="button" class="front-btn" id="resSecondary">${b.secondaryLabel}</button>` : ''}
        <button type="button" class="front-btn front-btn-primary" id="resPrimary">${b.primaryLabel}</button>
      </div>
    </div>`;
}

export type ResultsOptions = ResultsButtons & { onPrimary(): void; onSecondary?(): void };

/** Esc is the primary button. Returns the teardown; the caller owns `host`'s place in the page. */
export function mountResults(host: HTMLElement, d: ResultsData, opts: ResultsOptions) {
  const el = document.createElement('div');
  el.className = 'results';
  el.innerHTML = resultsHtml(d, { primaryLabel: opts.primaryLabel, secondaryLabel: opts.onSecondary ? opts.secondaryLabel : undefined });
  host.appendChild(el);
  const primary = el.querySelector('#resPrimary') as HTMLButtonElement;
  primary.addEventListener('click', () => opts.onPrimary());
  el.querySelector('#resSecondary')?.addEventListener('click', () => opts.onSecondary?.());
  const onKey = (e: KeyboardEvent) => {
    if (e.key !== 'Escape' || e.defaultPrevented) return;
    e.preventDefault();
    opts.onPrimary();
  };
  window.addEventListener('keydown', onKey);
  primary.focus?.();
  return {
    el,
    destroy() { window.removeEventListener('keydown', onKey); el.remove(); },
  };
}
